import { initDB } from "./db";
import { 
  JOBSITE_ADDRESS_STORE_NAME, 
  JOBSITE_DETAILS_STORE_NAME, 
  JOBSITE_FILES_STORE_NAME, 
  JOBSITE_TRADES_STORE_NAME, 
  JOBSITE_WORKERS_STORE_NAME,
  USERS_STORE_NAME,
  ROLES_STORE_NAME,
  REQUESTS_STORE_NAME,
  CONTRACTORS_STORE_NAME,
  JOBSITE_COMMENTS_STORE_NAME,
  JOBSITE_MARK_AS_COMPLETE_STORE_NAME,
  JOBSITE_SUBMIT_STORE_NAME,
  JOBSITE_DELETE_STORE_NAME,
  JOBSITE_FILES_DELETE_STORE_NAME
} from "./dbConstants";

// Core stores used in the application
export const CORE_STORES = [
  USERS_STORE_NAME,
  ROLES_STORE_NAME,
  REQUESTS_STORE_NAME
];

// Jobsite-related stores
export const JOBSITE_STORES = [
  JOBSITE_DETAILS_STORE_NAME,
  JOBSITE_ADDRESS_STORE_NAME,
  JOBSITE_WORKERS_STORE_NAME,
  JOBSITE_FILES_STORE_NAME,
  JOBSITE_TRADES_STORE_NAME,
  JOBSITE_COMMENTS_STORE_NAME,
  JOBSITE_MARK_AS_COMPLETE_STORE_NAME,
  JOBSITE_SUBMIT_STORE_NAME,
  JOBSITE_DELETE_STORE_NAME,
  JOBSITE_FILES_DELETE_STORE_NAME
];

// All important stores
export const ALL_IMPORTANT_STORES = [
  ...JOBSITE_STORES,
  CONTRACTORS_STORE_NAME
];

/**
 * Checks if a specific IndexedDB store is empty
 * @param storeName - Name of the store to check
 * @returns Promise that resolves to a boolean (true if store is empty, false otherwise)
 */
export async function isStoreEmpty(storeName: string): Promise<boolean> {
  try {
    const db = await initDB();
    
    // Check if store exists first
    if (!db.objectStoreNames.contains(storeName)) {
      return true; // Consider non-existent stores as empty
    }
    
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    
    // We use count() instead of getAll() for better performance
    const count = await store.count();
    return count === 0;
  } catch (error) {
    // In case of error, we assume the store might have data
    return false;
  }
}

/**
 * Checks if multiple stores are empty
 * @param storeNames - Array of store names to check
 * @returns Promise that resolves to an object with store names as keys and boolean values
 */
export async function checkMultipleStoresEmpty(storeNames: string[]): Promise<Record<string, boolean>> {
  const result: Record<string, boolean> = {};
  
  await Promise.all(
    storeNames.map(async (storeName) => {
      result[storeName] = await isStoreEmpty(storeName);
    })
  );
  
  return result;
}

/**
 * Checks if all data is empty across all stores
 * @returns Promise that resolves to a boolean (true if all stores are empty, false if any store has data)
 */
export async function isAllDataEmpty(): Promise<boolean> {
  try {
    const db = await initDB();
    const storeNames = Array.from(db.objectStoreNames);
    
    const emptyStatuses = await checkMultipleStoresEmpty(storeNames);
    
    // Return true only if all stores are empty
    return Object.values(emptyStatuses).every(isEmpty => isEmpty === true);
  } catch (error) {
    // In case of error, assume data exists
    return false;
  }
}

/**
 * Checks if all data is empty across all stores
 * @returns Promise that resolves to a boolean (true if all stores are empty, false if any store has data)
 */
export async function isAllJobsiteDependenciesStoresDataEmpty(storeNames: string[]): Promise<boolean> {
  try {
    
    const emptyStatuses = await checkMultipleStoresEmpty(storeNames);
    
    // Return true only if all stores are empty
    return Object.values(emptyStatuses).every(isEmpty => isEmpty === true);
  } catch (error) {
    // In case of error, assume data exists
    return false;
  }
}

/**
 * Checks if core application data (users, roles, etc.) is available
 * @returns Promise that resolves to a boolean (true if core data exists, false otherwise)
 */
export async function hasCoreData(): Promise<boolean> {
  const emptyStatuses = await checkMultipleStoresEmpty(CORE_STORES);
  // Return true if at least one core store has data
  return Object.values(emptyStatuses).some(isEmpty => isEmpty === false);
}

/**
 * Checks if jobsite data is available
 * @param jobsiteId - Optional jobsite ID to check for specific jobsite data
 * @returns Promise that resolves to a boolean or object with detailed information
 */
export async function hasJobsiteData(jobsiteId?: string): Promise<boolean | Record<string, any>> {
  if (jobsiteId) {
    // Check for specific jobsite data
    const result: Record<string, boolean> = {};
    
    const db = await initDB();
    
    // For each jobsite store, check if data exists for this jobsite ID
    await Promise.all(
      JOBSITE_STORES.map(async (storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          result[storeName] = false;
          return;
        }
        
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const allData = await store.getAll();
        
        // For each store, check if any record matches this jobsite ID
        // Different stores might use different fields (id, job_id, jobId)
        result[storeName] = allData.some((item: any) => 
          item.id === jobsiteId || 
          item.job_id === jobsiteId || 
          item.jobId === jobsiteId
        );
      })
    );
    
    return {
      hasAnyData: Object.values(result).some(hasData => hasData === true),
      details: result
    };
  } else {
    // Just check if any jobsite stores have data
    const emptyStatuses = await checkMultipleStoresEmpty(JOBSITE_STORES);
    return Object.values(emptyStatuses).some(isEmpty => isEmpty === false);
  }
}

/**
 * Gets a count of records in a store
 * @param storeName - Name of the store to check
 * @returns Promise that resolves to a number representing the count of records
 */
export async function getStoreCount(storeName: string): Promise<number> {
  try {
    const db = await initDB();
    
    if (!db.objectStoreNames.contains(storeName)) {
      return 0;
    }
    
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    
    return await store.count();
  } catch (error) {
    return 0;
  }
}

/**
 * Gets counts for all stores
 * @returns Promise that resolves to an object with store names as keys and counts as values
 */
export async function getAllStoreCounts(): Promise<Record<string, number>> {
  try {
    const db = await initDB();
    const storeNames = Array.from(db.objectStoreNames);
    const result: Record<string, number> = {};
    
    await Promise.all(
      storeNames.map(async (storeName) => {
        result[storeName] = await getStoreCount(storeName);
      })
    );
    
    return result;
  } catch (error) {
    return {};
  }
}

/**
 * Gets a data availability summary for the application
 * @returns Promise that resolves to an object with detailed information about data availability
 */
export async function getDataAvailabilitySummary(): Promise<Record<string, any>> {
  const allStoreCounts = await getAllStoreCounts();
  const coreDataAvailable = await hasCoreData();
  const jobsiteDataAvailable = await hasJobsiteData();
  
  return {
    hasData: Object.values(allStoreCounts).some(count => count > 0),
    hasCoreData: coreDataAvailable,
    hasJobsiteData: jobsiteDataAvailable,
    storeCounts: allStoreCounts,
    totalRecords: Object.values(allStoreCounts).reduce((sum, count) => sum + count, 0)
  };
} 